//#region [IMPORT]

// === THIRD-PARTY ===
import { useRef, useState } from 'react';

// ### MUI / STYLES ###
import { useTheme } from '@mui/material/styles';
import {
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Settings,
} from '@mui/icons-material';

// === COMPONENTS ===
import Styled from 'components/Styled/Styled';
import Collapsible from 'components/Collapsible';
import { DragHandle  } from "components/DragDrop/DragHandle";
import { GroupDrag   } from 'components/DragDrop/GroupDrag';
import { ResizableGrid } from "../ResizableGrid/ResizableGrid";
import { useLayout } from "./PanelContext";
import { PanelDirection, PanelType } from "./PanelData";
import { DocumentCategoriesList } from "../DocumentCategoriesList/DocumentCategoriesList";
import { DocumentReview } from "../DocumentReview/DocumentReviews";
import { FileComments } from "../FileComments/FileComments";
import { ThumbPanel } from "../ThumbPanel/ThumbPanel";
import { ImagePanel } from "../ImagePanel/ImagePanel";
import { getHexAlpha } from 'components/styles.utils';

//#endregion

//-----------------------------------------------------------------
export function Panel( props ) {
  //-----------------------------------------------------------------
  //#region [VALUES]

  const { id } = props;
  const theme = useTheme();
  const {
    getParent,
    getItem, moveItem, openItem, closeItem,
    getSizes, setSizes,
    removeItem, createItem,
    changeDirection, setType,
    getAvailablePanels,
  } = useLayout();

  const data   = getItem  (id);
  const parent = getParent(id);

  const [ anchor_settings , setAnchorSettings ] = useState(null);
  const [ anchor_change   , setAnchorChange   ] = useState(null);
  const [ anchor_add      , setAnchorAdd      ] = useState(null);

  const panel_names = new Map([
    [PanelType.PANEL            , 'Panel'                 ],
    [PanelType.DOCUMENTS        , 'Documents'             ],
    [PanelType.REVIEW           , 'Fields'                ],
    [PanelType.IMAGE            , 'Image'                 ],
    [PanelType.THUMB            , 'Thumbnails'            ],
    [PanelType.COMPARISON_IMAGE , 'Comparison Image'      ],
    [PanelType.COMPARISON_THUMB , 'Comparison Thumbnails' ],
    [PanelType.COMPARISON_FIELD , 'Comparison Fields'     ],
    [PanelType.COMMENTS         , 'Comments'              ],
  ])

  //#endregion

  //#region [STYLES]

  const bg = theme.palette.background.primary[theme.palette.mode]+getHexAlpha(80);
  const MENU_MAIN = {
    PaperProps      : {
      style: {
        margin: '0', padding:'0px', minWidth: '150px',
        background: bg, backdropFilter: 'blur(30px)',
        // background: `radial-gradient(circle, #2e2e2e57 300px, ${bg} 10px)`,    
        border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '6px'
      }
    },
    MenuListProps   : {
      disablePadding: true, // Removes top/bottom padding of the list
    },
    open            : anchor_settings !== null,
    onClose         : closeMenuSettings,
    anchorEl        : anchor_settings,
    anchorOrigin    : {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin : {
      vertical: 'top',
      horizontal: 'center',
    }
  }
  const MENU_CHANGE = {
    PaperProps      : {
      style: {
        margin: '0', padding:'0px', minWidth: '150px',
        background: bg, backdropFilter: 'blur(30px)',
        // background: `radial-gradient(circle, #2e2e2e57 300px, ${bg} 10px)`,    
        border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '6px'
      }
    },
    MenuListProps   : {
      disablePadding: true, // Removes top/bottom padding of the list
    },
    open            : anchor_change !== null,
    onClose         : closeMenuChange,
    anchorEl        : anchor_change,
    anchorOrigin    : {
      vertical: 'top',
      horizontal: 'left',
    },
    transformOrigin : {
      vertical: 'top',
      horizontal: 'right',
    }
  }
  const MENU_ADD = {
    PaperProps      : {
      style: {
        margin: '0', padding:'0px', minWidth: '150px',
        background: bg, backdropFilter: 'blur(30px)',
        // background: `radial-gradient(circle, #2e2e2e57 300px, ${bg} 10px)`,    
        border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: '6px'
      }
    },
    MenuListProps   : {
      disablePadding: true, // Removes top/bottom padding of the list
    },
    open            : anchor_add !== null,
    onClose         : closeMenuAdd,
    anchorEl        : anchor_add,
    anchorOrigin    : {
      vertical: 'top',
      horizontal: 'left',
    },
    transformOrigin : {
      vertical: 'top',
      horizontal: 'right',
    }
  }

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function handleClick_Settings(event) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.preventDefault();
    setAnchorSettings( event.currentTarget );
  };

  //-----------------------------------------------------------------
  function handleClick_ChangeDir(event) {
    //-----------------------------------------------------------------
    closeMenuSettings(event);

    changeDirection( id );
  }

  //-----------------------------------------------------------------
  function handleClick_Change(event) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.preventDefault();
    setAnchorChange( event.currentTarget );
  }

  //-----------------------------------------------------------------
  function handleClick_Create(event) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.preventDefault();
    setAnchorAdd( event.currentTarget );
  }

  //-----------------------------------------------------------------
  function handleClick_Remove(event) {
    //-----------------------------------------------------------------
    closeMenuSettings(event);

    removeItem( id )
  }

  //-----------------------------------------------------------------
  function handleClick_PanelTypeChange(event, type) {
    //-----------------------------------------------------------------
    closeMenuChange(event);

    setType( id, type )
  }

  //-----------------------------------------------------------------
  function handleClick_PanelTypeAdd(event, type) {
    //-----------------------------------------------------------------
    closeMenuAdd(event);

    createItem( id, type );
  }

  //-----------------------------------------------------------------
  function closeMenuSettings(event) {
    //-----------------------------------------------------------------
    event?.stopPropagation();
    event?.preventDefault();
    setAnchorSettings( null );
  }

  //-----------------------------------------------------------------
  function closeMenuChange(event) {
    //-----------------------------------------------------------------
    event?.stopPropagation();
    event?.preventDefault();
    setAnchorChange( null );
  }

  //-----------------------------------------------------------------
  function closeMenuAdd(event) {
    //-----------------------------------------------------------------
    event?.stopPropagation();
    event?.preventDefault();
    setAnchorAdd( null );
  }

  //#endregion

  //#region [RENDER]

  function display() {
    if( data == null ) return null;
    if( data.type === PanelType.ROOT ) {
      return displayChildren()
    }

    const drag_type = data.type === PanelType.ROOT ? 'root' : (data.type === PanelType.PANEL ? 'panel' : 'view');
    const header = getHeader();
    return (
      <GroupDrag data_type={drag_type} data={{id: data.id, index: props.index}} styles={[]} moveItem={moveItem} direction={parent.direction}>
        <Collapsible
          styles        = {[]}
          direction     = {parent.direction}
          is_open       = {data.is_open}
          handleOpen    = {()=> openItem(id)}
          handleClose   = {()=>closeItem(id)}
          header        = {header}
          header_closed = {header}
          body          = {{
            children: displayBody(),
            styles  : [{overflow:'auto'}]}}
          body_closed   = {{
            children: displayBody(),
            styles  : []
          }}
        />
      </GroupDrag>
    )
  }

  //-----------------------------------------------------------------
  function getHeader() {
    //-----------------------------------------------------------------
    if( data.type === PanelType.ROOT ) return null;
    const is_col = parent.direction === PanelDirection.COL;
    let name = '';
    let header_styles = {};
    switch( data.type ) {
      case PanelType.DOCUMENTS : name = 'Documents'; break;
      case PanelType.COMMENTS  : name = 'Comments'; break;
      case PanelType.REVIEW    : name = 'Fields'; break;
      case PanelType.THUMB     : name = 'Thumbnails'; break;
      case PanelType.IMAGE     : name = 'Image'; break;
      case PanelType.COMPARISON_THUMB : name = 'Comparison Thumbnails'; break;
      case PanelType.COMPARISON_IMAGE : name = 'Comparison Image'; break;
      case PanelType.COMPARISON_FIELD : name = 'Comparison Fields'; break;
      case PanelType.PANEL     : header_styles = { borderBottom: '2px solid '+theme.palette.primary.main}; break;
    }
    const header_settings = (
      <Styled styles={[{marginleft: 'auto'},{width:'fit-content'}]} onClick={()=>{}} >
        <Settings />
      </Styled>
    )
    const styles_inner = [{display:'flex'}];
    if( !data.is_open ) {
      if( is_col ) styles_inner.push( {height:'100%', width: '25px'} );
    }
    const header_body = (
      <Styled styles={styles_inner}>
        <DragHandle styles={[!data.is_open && is_col?{height:'60px'}:{}]} />
        <Styled styles={[ {fontWeight: 'bold'} ]}>
          {name}
        </Styled>
        {displayPanelSettings()}
      </Styled>
    )
    const styles_outer = [{ background: theme.palette.background.secondary[theme.palette.mode] },header_styles];
    if( !data.is_open ) {
      if( is_col ) {
        styles_outer.push( { height:'100%', width: '25px' } );
        styles_outer.push( { alignItems:'center', justifyContent: 'center', writingMode: 'vertical-rl', textOrientation:'upright'} );
      }
    }

    const header = {
      children: header_body,
      styles  : styles_outer
    };

    return header;
  }

  //-----------------------------------------------------------------
  function displayBody() {
    //-----------------------------------------------------------------
    if( data.type === PanelType.PANEL || data.type === PanelType.ROOT ) {
      return displayChildren();
    }

    let body = null;
    switch( data.type ) {
      case PanelType.DOCUMENTS : body = <DocumentCategoriesList />; break;
      case PanelType.COMMENTS  : body = <FileComments />; break;
      case PanelType.REVIEW    : body = <DocumentReview />; break;
      case PanelType.THUMB     : body = <ThumbPanel />; break;
      case PanelType.IMAGE     : body = <ImagePanel />; break;
      case PanelType.COMPARISON_THUMB : body = <ThumbPanel     is_comp={true} />; break;
      case PanelType.COMPARISON_IMAGE : body = <ImagePanel     is_comp={true} />; break;
      case PanelType.COMPARISON_FIELD : body = <DocumentReview is_comp={true} />; break;
    }

    return body;
  }

  //-----------------------------------------------------------------
  function displayChildren() {
    //-----------------------------------------------------------------
    return (
      <ResizableGrid
        style={{ height: "100%", width: "100%" }}
        columns={data.col_count}
        rows={data.row_count}
        sizes={getSizes(id)}
        resizable="both"
        dividerSize={8}
        dividerColor={theme.palette.primary.main}
        dividerTrim={theme.palette.background.primary[theme.palette.mode]}
        onChange={(sizes)=>{ setSizes(id,sizes) }}
        renderCell={(r, c, index) => {
          return <Panel id={data.items[index]} index={index} />
        }}
      />
    )
  }

  //-----------------------------------------------------------------
  function displayPanelSettings() {
    //-----------------------------------------------------------------
    const available_panels = getAvailablePanels();
    
    return (
      <Styled styles={[{marginleft: 'auto'},{width:'fit-content'}]}>
        <Styled onClick={handleClick_Settings} >
          <Settings  />
        </Styled>
        <Menu { ...MENU_MAIN } >
          {data.type !== PanelType.PANEL ? null : <MenuItem sx={{ margin: '4px', padding: '4px 8px 4px 8px', borderRadius: '4px' }} onClick={handleClick_ChangeDir} >Change Direction</MenuItem>}
          {data.type !== PanelType.PANEL ? null : <li style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.3)', margin: '4px 12px 4px 12px' }} />}
          {data.is_locked ? null : <MenuItem sx={{ margin: '4px', padding: '4px 8px 4px 8px', borderRadius: '4px' }} onClick={handleClick_Change} >Change Panel</MenuItem>}
          {data.type !== PanelType.PANEL ? null : <li style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.3)', margin: '4px 12px 4px 12px' }} />}
          {data.type !== PanelType.PANEL ? null : <MenuItem sx={{ margin: '4px', padding: '4px 8px 4px 8px', borderRadius: '4px' }} onClick={handleClick_Create} >Add Panel</MenuItem>}
          {data.is_locked ? null : <li style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.3)', margin: '4px 12px 4px 12px' }} />}
          {data.is_locked ? null : <MenuItem sx={{ margin: '4px', padding: '4px 8px 4px 8px', borderRadius: '4px' }} onClick={handleClick_Remove}>Remove Panel</MenuItem>}
        </Menu>
        <Menu { ...MENU_CHANGE }>
          {
            available_panels?.map( type => {
              return <MenuItem onClick={(event)=>handleClick_PanelTypeChange( event, type )}>{ panel_names.get(type) }</MenuItem>
            })
          }
        </Menu>
        <Menu { ...MENU_ADD }>
          {
            available_panels?.map( type => {
              return <MenuItem onClick={(event)=>handleClick_PanelTypeAdd( event, type )}>{ panel_names.get(type) }</MenuItem>
            })
          }
        </Menu>
      </Styled>
    )
  }

  //#endregion

  return display();
}


function SubMenuItem() {

}